<template>
    <section class="hero-section">

        <div class="hero-grid">

            <p v-if="!featuredPost" class="text-lg">
                {{ translate('No posts found.') }}
            </p>
            <div class="hero-main" v-if="featuredPost">

                <div class="hero-image-wrapper">

                    <img :src="featuredPost?.getPhoto()" class="hero-image" />

                    <div class="hero-gradient"></div>
                    <div class="hero-content">

                        <div class="featured-tags-wrapper">
                            <Tag 
                                v-for="tag in featuredPost?.getTags().slice(0, 4)" 
                                :key="tag" 
                                :value="tag" 
                                class="featured-tag"
                                rounded 
                            />
                        </div>

                        <h1>
                            {{ featuredPost?.title }}
                        </h1>

                        <p>
                            {{ featuredPost?.getShortenedText() }}
                        </p>

                        <div class="hero-meta">

                            <div class="meta-item">
                                <i class="pi pi-user"></i>
                                {{ featuredPost?.getAuthorName() }}
                            </div>

                            <div class="meta-item">
                                <i class="pi pi-clock"></i>
                                {{ featuredPost?.getReadingTime() }} {{ translate('min read') }}
                            </div>

                            <div class="meta-item">
                                <i class="pi pi-calendar"></i>
                                {{ featuredPost?.getCreatedDate() }}
                            </div>

                        </div>

                        <Button 
                            :label="translate('read article', 'capitalize')" 
                            icon="pi pi-arrow-right" 
                            iconPos="right" 
                            class="hero-btn" 
                            @click="urlHandler.redirectAndScroll(`/blog/detail/${featuredPost.id}`)"  
                        />

                    </div>

                </div>

            </div>

            <div class="hero-side">

                <div v-for="post in sidePosts" :key="post.id" class="mini-post">

                    <img :src="post.getPhoto()" class="mini-image" />

                    <div class="mini-content">

                        <div class="mini-tags">

                            <Tag v-for="tag in post.getTags().slice(0, 4)" :key="tag" :value="tag" rounded class="featured-tag" />

                        </div>

                        <h3>
                            {{ post.title }}
                        </h3>

                        <p>
                            {{ post.getShortenedText() }}
                        </p>

                        <div class="mini-footer">

                            <span>
                                {{ post.getReadingTime() }} {{ translate('min read') }}
                            </span>

                            <Button icon="pi pi-arrow-right" rounded text @click="urlHandler.redirectAndScroll(`/blog/detail/${post.id}`)" />

                        </div>

                    </div>

                </div>

            </div>

        </div>

    </section>
</template>

<script setup>

import { computed, onMounted, ref } from "vue";

import Button from "primevue/button";
import Tag from "primevue/tag";
import BlogPostService from "@/services/BlogPostService";
import { translate } from "@/composables/translate";
import urlHandler from "@/composables/urlHandler";

onMounted(() => {
    loadPosts();
})

const posts = ref([]);
async function loadPosts() {
    posts.value = await BlogPostService.get();
}

/**
 * Featured
 */
const featuredPost = computed(() => {
    return posts.value?.[0] || null;
});

/**
 * Side posts
 */
const sidePosts = computed(() => {
    return posts.value?.slice(1, 4) || [];
});

</script>

<style scoped>
/* HERO */

.hero-section {
    padding: 2rem;
}

.hero-grid {
    display: grid;
    grid-template-columns: 1.6fr 0.9fr;
    gap: 1.5rem;
    align-items: stretch;
}

/* LEFT BIG CARD */

.hero-image-wrapper {
    position: relative;
    height: 760px;
    overflow: hidden;
    border-radius: 28px;
    background: #000;
}

.hero-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.hero-gradient {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(to top,
            rgba(0, 0, 0, 0.88),
            rgba(0, 0, 0, 0.3),
            rgba(0, 0, 0, 0));
}

.hero-content {
    position: absolute;
    left: 0;
    bottom: 0;
    width: 100%;
    padding: 2.5rem;
    z-index: 2;
}

.featured-tag {
    background: #981515 !important;
    border: none !important;
    font-size: 0.75rem;
    padding: 0.4rem 0.9rem;
}

.hero-content h1 {
    margin-top: 1.5rem;
    color: #f7f1e7;
    font-size: 5.5rem;
    line-height: 0.88;
    max-width: 650px;
    font-family: 'Anton', sans-serif;
    letter-spacing: 1px;
}

.hero-content p {
    margin-top: 1rem;
    max-width: 560px;
    color: rgba(255, 255, 255, 0.9);
    font-size: 1.1rem;
    line-height: 1.6;
}

.hero-meta {
    margin-top: 1.4rem;
    display: flex;
    gap: 1.5rem;
    flex-wrap: wrap;
}

.meta-item {
    display: flex;
    align-items: center;
    gap: 0.45rem;
    color: rgba(255, 255, 255, 0.92);
    font-size: 0.95rem;
}

.hero-btn {
    margin-top: 2rem;
    background: linear-gradient(135deg,
            #ea6a1c,
            #ff8b28) !important;

    border: none !important;
    padding: 1rem 1.6rem;
    border-radius: 999px;
    font-weight: 700;
}

/* RIGHT POSTS */

.hero-side {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.mini-post {
    background: #f7eedf;
    border: 1px solid #e5c9ac;
    border-radius: 24px;
    padding: 1rem;
    display: flex;
    gap: 1rem;
    min-height: 240px;
}

.mini-image {
    width: 42%;
    border-radius: 18px;
    object-fit: cover;
}

.mini-content {
    flex: 1;
    display: flex;
    flex-direction: column;
}

.mini-tags {
    display: flex;
    gap: 0.4rem;
    flex-wrap: wrap;
}

.mini-tags :deep(.p-tag) {
    background: #9c1717 !important;
    border: none !important;
    font-size: 0.62rem;
    color: #ffffff;
}

.mini-content h3 {
    margin-top: 0.8rem;
    color: #7d140f;
    font-size: 2rem;
    line-height: 0.95;
    font-family: 'Anton', sans-serif;
}

.mini-content p {
    margin-top: 0.7rem;
    color: #5d3529;
    line-height: 1.5;
    font-size: 0.95rem;
}

.mini-footer {
    margin-top: auto;
    display: flex;
    justify-content: space-between;
    align-items: center;
    color: #7a4c3a;
    font-weight: 500;
}

.featured-tags-wrapper {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: -0.5rem; /* Compensa o margin-top do h1 */
}

/* Estilização ajustada mantendo a consistência visual */
.featured-tags-wrapper :deep(.p-tag) {
    background: #9c1717 !important;
    border: none !important;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;
    letter-spacing: 0.15em;
    padding: 0.5rem 1rem;
    color: #ffffff;
}

/* MOBILE */

@media (max-width: 1200px) {

    .hero-grid {
        grid-template-columns: 1fr;
    }

    .hero-image-wrapper {
        height: 650px;
    }

}

@media (max-width: 768px) {

    .hero-content h1 {
        font-size: 3.5rem;
    }

    .hero-image-wrapper {
        height: 540px;
    }

    .mini-post {
        flex-direction: column;
    }

    .mini-image {
        width: 100%;
        height: 180px;
    }

}
</style>